/// <amd-module name="TFS/Admin/Views/ProjectGroups/Components/ProjectsPageContentView" />
import * as React from "react";
import { connect } from "react-redux";
import { Area, Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import * as Constants from "TFS/Admin/Groups/Common/Constants";
import { GroupsNavType } from "TFS/Admin/Groups/Common/Constants";
import { CustomGraphMember, IGroupsViewState, PermissionsGroup } from "TFS/Admin/Groups/Common/Contracts";
import { GroupsPivotActions } from "TFS/Admin/Groups/Common/Redux/Actions/GroupsPivotActions";
import { GroupsViewActions } from "TFS/Admin/Groups/Common/Redux/Actions/GroupsViewActions";
import { MemberOfPivotActions } from "TFS/Admin/Groups/Common/Redux/Actions/MemberOfPivotActions";
import { GroupMembersPivotActions } from "TFS/Admin/Groups/Common/Redux/Actions/GroupMembersPivotActions";
import * as ViewSelectors from "TFS/Admin/Groups/Common/Redux/GroupsViewSelectors";
import * as Resources from "TFS/Admin/Groups/Common/Resources";
import { convertGraphMemberToIIdentity } from "TFS/Admin/Groups/Common/Util/CommonTypeConverters";
import { GroupsComponent } from "TFS/Admin/Groups/Common/Util/GroupsTelemetryLogger";
import { IObservableArray, ObservableValue } from "VSS/Core/Observable";
import { ContributedTabProvider } from "VSS/Features/Frame/ContributedTabProvider";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { GraphServicePrincipal, GraphUser } from "VSS/Platform/Contracts/Graph";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { FilterBar } from "VSSUI/FilterBar";
import { CustomHeader, HeaderDescription, HeaderTitle, HeaderTitleArea, HeaderTitleRow, TitleSize } from "VSSUI/Header";
import { Page } from "VSSUI/Page";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { IVssContributedTab, TabBar, TabContent } from "VSSUI/Tabs";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { IHubViewState } from "VSSUI/Utilities/HubViewState";
import { IVssPersonaProps, VssPersona } from "VSSUI/VssPersona";

interface IProjectGroupsContainerProps {
    hubViewState: IHubViewState;
    context: IVssComponentContext;
}

interface IProjectsPageContentViewOwnProps extends IProjectGroupsContainerProps {
    selectedGroup: PermissionsGroup | undefined;
    selectedUser: GraphUser | undefined;
    selectedServicePrincipal: GraphServicePrincipal | undefined;
    selectedMember: CustomGraphMember | undefined;
    selectedNavType: GroupsNavType;
    filterPlaceholder: string | undefined;
    updatedAvatarUrl?: string;
}

export type IProjectsPageContentViewProps = IProjectsPageContentViewOwnProps & typeof ActionsToDispatch;

interface ProjectsPageContentViewState {}

export class ProjectsPageContentView extends VssComponent<IProjectsPageContentViewProps, ProjectsPageContentViewState> {
    private _tabProviders: IObservableArray<IVssContributedTab>;
    private _pageStateHistoryService: IPageStateHistoryService;
    private _selectedSubjectDescriptor: ObservableValue<string>;
    private _selectedTab: ObservableValue<string>;

    public constructor(props: IProjectsPageContentViewProps) {
        super(props);

        this._selectedTab = new ObservableValue(this._getDefaultSelectedTab());
        this._pageStateHistoryService = this.props.context.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
        this._selectedSubjectDescriptor = new ObservableValue("");
        this._selectedSubjectDescriptor.subscribe(this.onSelectedSubjectDescriptorChanged);
        this._pageStateHistoryService.registerObservable(this._selectedSubjectDescriptor, "subjectDescriptor", HistoryBehavior.newEntry);
        this.props.initView();
    }

    public render(): JSX.Element {
        this._tabProviders = new ContributedTabProvider({
            pageContext: this.props.context.pageContext,
            tabGroupIds: this._getSelectedTabGroup(),
            updateUrlOnPivotChange: true,
            props: {
                filter: this.props.hubViewState.filter
            }
        });

        const selectedGraphSubject = this.props.selectedGroup ?? this.props.selectedUser ?? this.props.selectedServicePrincipal ?? this.props.selectedMember;
        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="flex-grow flex-column" tabProviders={this._tabProviders} selectedTabId={this._selectedTab}>
                    <CustomHeader className="bolt-header-with-commandbar">
                        {selectedGraphSubject && !this.props.updatedAvatarUrl ? (
                            <Persona
                                className="display-members-persona"
                                size={PersonaSize.size40}
                                identity={convertGraphMemberToIIdentity(
                                    this.props.context.pageContext,
                                    selectedGraphSubject,
                                    this.props.selectedGroup ? this.props.selectedGroup.updateImage : false
                                )}
                            />
                        ) : null}
                        {this.props.updatedAvatarUrl ? (
                            <VssPersona {...this._getPersona()} size="medium-plus" className="display-members-persona" />
                        ) : null}
                        <HeaderTitleArea>
                            <HeaderTitleRow>
                                <HeaderTitle className="text-ellipsis header-title-no-white-spaces" titleSize={TitleSize.Large}>
                                    {this._getSelectedSubjectName()}
                                </HeaderTitle>
                            </HeaderTitleRow>
                            <HeaderDescription>{this._getGroupDescription()}</HeaderDescription>
                        </HeaderTitleArea>
                    </CustomHeader>

                    <TabBar onSelectedTabChanged={this._onSelectedTabChanged} renderAdditionalContent={this._renderAdditionalContent} />
                    <div className="page-content page-content-top">
                        <TabContent />
                    </div>
                </Page>
            </Surface>
        );
    }

    public componentDidMount(): void {
        const performanceService: IVssPerformanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.markTimeToInteractive(Area.Admin, Feature.ProjectGroups);

        this.props.logTelemetry(GroupsComponent.GroupsView, "Page loaded", {});
    }

    private _getSelectedMemberDescriptor(): string | undefined {
        const { selectedGroup, selectedUser, selectedServicePrincipal, selectedMember } = this.props;
        return selectedGroup?.descriptor ?? selectedUser?.descriptor ?? selectedServicePrincipal?.descriptor ?? selectedMember?.descriptor ?? "";
    }

    public componentDidUpdate(prevProps: IProjectsPageContentViewProps) {
        const prevDescriptor = prevProps.selectedGroup?.descriptor 
                ?? prevProps.selectedUser?.descriptor 
                ?? prevProps.selectedServicePrincipal?.descriptor 
                ?? prevProps.selectedMember?.descriptor 
                ?? "";
        const selectedDescriptor =  this._getSelectedMemberDescriptor();

        if (prevDescriptor != selectedDescriptor || prevProps.selectedNavType != this.props.selectedNavType) {
            this._onSelectedTabChanged(this._getDefaultSelectedTab()); // Get default tab for groups-details and user-details
        }

        if (selectedDescriptor && this._selectedSubjectDescriptor.value != selectedDescriptor) {
            this._selectedSubjectDescriptor.value = selectedDescriptor;
        }
    }

    private _getPersona(): IVssPersonaProps {
        const avatarPersona = {
            suppressPersonaCard: true,
            displayName: this._getSelectedSubjectName(),
            showInitialsOnImageError: true,
            imageUrl: "",
            imgAltText: ""
        };

        if (this.props.updatedAvatarUrl) {
            (avatarPersona.imageUrl = this.props.updatedAvatarUrl), (avatarPersona.imgAltText = Resources.ImageAlt);
        }

        return avatarPersona;
    }

    // This will get called on view switch from UI and when browser navigation is used
    private onSelectedSubjectDescriptorChanged = (value: string, action: string): void => {
        const subjectDescriptor = this._getSelectedMemberDescriptor();
        if (subjectDescriptor && subjectDescriptor != value) {
            this.props.initGroupsPivot();
        }
    };

    private _getGroupDescription(): string {
        const { selectedGroup } = this.props;
        return selectedGroup ? selectedGroup.description : "";
    }

    private _getSelectedSubjectName(): string {
        const { selectedGroup, selectedUser, selectedServicePrincipal, selectedMember } = this.props;
        return selectedGroup?.principalName 
            ?? selectedUser?.displayName
            ?? selectedServicePrincipal?.displayName
            ?? selectedMember?.displayName
            ?? Resources.PermissionsPageHeader;
    }

    private _onSelectedTabChanged = (newTabId: string) => {
        this.props.logTelemetry(GroupsComponent.GroupsView, "Selected tab changed", { NewTabId: newTabId });
        this._selectedTab.value = newTabId;
    };

    private _getDefaultSelectedTab(): string {
        switch (this.props.selectedNavType) {
            case GroupsNavType.GroupDetails:
            case GroupsNavType.UserDetails:
            case GroupsNavType.ServicePrincipalDetails:
            case GroupsNavType.MemberDetails:
                return Constants.permissionsTab;

            default:
                return Constants.groupsTab;
        }
    }

    private _getSelectedTabGroup(): string[] {
        switch (this.props.selectedNavType) {
            case GroupsNavType.GroupDetails:
                return [Constants.GroupsPageDetailsTabGroup];

            case GroupsNavType.UserDetails:
            case GroupsNavType.ServicePrincipalDetails:
                case GroupsNavType.MemberDetails:
                return [Constants.UsersPageDetailsTabGroup];

            default:
                return [Constants.GroupsPageTabGroup];
        }
    }

    private _renderAdditionalContent = (): JSX.Element | undefined => {
        if ([Constants.groupsTab, Constants.usersTab, Constants.servicePrincipalsTab, Constants.membersTab, Constants.groupMembersTab, Constants.memberOfTab].indexOf(this._selectedTab.value) !== -1) {
            return (
                <FilterBar filter={this.props.hubViewState.filter}>
                    <KeywordFilterBarItem
                        placeholder={this.props.filterPlaceholder || Resources.FilterUsersAndGroups}
                        filterItemKey={Constants.SearchKeyword}
                    />
                </FilterBar>
            );
        }

        return undefined;
    };
}

// Update component props whenever the store's state changes
function mapStateToProps(state: IGroupsViewState, providedProps: IProjectGroupsContainerProps): Partial<IProjectsPageContentViewProps> {
    return {
        ...providedProps,
        selectedGroup: ViewSelectors.getSelectedGroup(state),
        selectedUser: ViewSelectors.getSelectedUser(state),
        selectedNavType: ViewSelectors.getSelectedNavType(state),
        filterPlaceholder: ViewSelectors.setFilterPlaceholder(state),
        updatedAvatarUrl: ViewSelectors.getUpdatedAvatarUrl(state)
    };
}

// Hook up action creators to reducer
const ActionsToDispatch = {
    initView: GroupsViewActions.initView,
    logTelemetry: GroupsViewActions.logTelemetry, // TODO - update the telemetry to Feature.ProjectGroups
    closeAddGroupPanel: GroupsPivotActions.closeAddGroupPanel,
    openAddMemberPanel: GroupMembersPivotActions.openAddMemberPanel,
    openAddMemberOfPanel: MemberOfPivotActions.openAddMemberOfPanel,
    initGroupsPivot: GroupsPivotActions.initView
};

export const ConnectedProjectsPageContentView = connect(mapStateToProps, ActionsToDispatch)(ProjectsPageContentView);
